// services/clientService.js - Servicio para clientes y cuentas
import apiClient from './api';
import { toast } from 'react-toastify';

class ClientService {
  // Método auxiliar para manejar errores del API
  handleApiError(error, defaultMessage = 'Error en la operación') {
    if (!error.response) {
      // Error de conectividad
      toast.error('Error de conexión. Verifique su conexión a internet.', {
        theme: 'colored'
      });
      return 'Error de conexión';
    }

    const errorData = error.response.data;
    
    // Si hay múltiples errores de validación
    if (errorData.message && Array.isArray(errorData.message)) {
      const errorList = errorData.message.map((msg, index) => `${index + 1}. ${msg}`).join('\n');
      toast.error(
        <div>
          <strong>Errores encontrados:</strong>
          <br />
          <div style={{ whiteSpace: 'pre-line', marginTop: '8px' }}>
            {errorList}
          </div>
        </div>, 
        { 
          theme: 'colored',
          autoClose: 8000 // Más tiempo para leer múltiples errores
        }
      );
      return errorList;
    }

    // Error simple
    const message = errorData.message || defaultMessage;
    toast.error(message, { theme: 'colored' });
    return message;
  }

  // === CLIENTES ===

  // Obtener todos los clientes activos
  async getAllClients() {
    try {
      const response = await apiClient.get('/budget/clients');
      return { success: true, clients: response.data.data };
    } catch (error) {
      console.error('Error al obtener clientes:', error);
      const message = this.handleApiError(error, 'Error al obtener clientes');
      return { success: false, error: message };
    }
  }

  // Obtener clientes con sus cuentas
  async getClientsWithAccounts() {
    try {
      const response = await apiClient.get('/budget/clients/with-accounts');
      return { success: true, clients: response.data.data };
    } catch (error) {
      const message = this.handleApiError(error, 'Error al obtener clientes con cuentas');
      return { success: false, error: message };
    }
  }

  // Obtener cliente por ID
  async getClientById(clientId) {
    try {
      const response = await apiClient.get(`/budget/clients/${clientId}`);
      return { success: true, client: response.data.data };
    } catch (error) {
      const message = this.handleApiError(error, 'Error al obtener cliente');
      return { success: false, error: message };
    }
  }

  // === CLIENTES ===

  // Crear nuevo cliente
  async createClient(clientData) {
    try {
      const response = await apiClient.post('/budget/clients', clientData);
      
      if (response.data?.success) {

        toast.success('Cliente creado exitosamente', { theme: 'colored' });
        return { success: true, client: response.data.data };
      }
      
      return response.data;
    } catch (error) {
      console.error('Error creating client:', error);
      const message = this.handleApiError(error, 'Error al crear cliente');
      return { success: false, error: message };
    }
  }

  // Actualizar cliente existente
  async updateClient(clientId, clientData) {
    try {
      const response = await apiClient.put(`/budget/clients/${clientId}`, clientData);
      
      if (response.data?.success) {

        toast.success('Cliente actualizado exitosamente', { theme: 'colored' });
        return { success: true, client: response.data.data };
      }
      
      return response.data;
    } catch (error) {
      console.error('Error updating client:', error);
      const message = this.handleApiError(error, 'Error al actualizar cliente');
      return { success: false, error: message };
    }
  }

  // Eliminar cliente
  async deleteClient(clientId) {
    try {
      const response = await apiClient.delete(`/budget/clients/${clientId}`);
      
      if (response.data?.success) {
        const { deletedClient, deletedAccountsCount, message } = response.data.data;
        const successMessage = message || `Cliente "${deletedClient?.ClientName}" eliminado exitosamente`;
        
        toast.success(successMessage, { theme: 'colored' });
        return { 
          success: true, 
          message: successMessage,
          deletedClient,
          deletedAccountsCount
        };
      }
      
      return response.data;
    } catch (error) {
      console.error('Error deleting client:', error);
      const message = this.handleApiError(error, 'Error al eliminar cliente');
      return { success: false, error: message };
    }
  }

  // === CUENTAS ===

  // Obtener cuentas de un cliente específico
  async getAccountsByClientId(clientId) {
    try {
      const response = await apiClient.get(`/budget/clients/${clientId}/accounts`);
      return { success: true, accounts: response.data.data };
    } catch (error) {
      console.error('Error al obtener cuentas del cliente:', error);
      const message = this.handleApiError(error, 'Error al obtener cuentas del cliente');
      return { success: false, error: message };
    }
  }

  // Obtener cuenta por ID
  async getAccountById(accountId) {
    try {
      const response = await apiClient.get(`/budget/accounts/${accountId}`);
      return { success: true, account: response.data.data };
    } catch (error) {
      const message = this.handleApiError(error, 'Error al obtener cuenta');
      return { success: false, error: message };
    }
  }

  // Crear nueva cuenta
  async create(accountData) {
    try {
      const response = await apiClient.post('/budget/accounts', accountData);
      
      // *** NUEVO: Disparar evento global de refresh de cuentas ***
      if (response.data?.success) {

        
        // Disparar evento global para invalidar cachés
        const refreshEvent = new CustomEvent('accounts:refresh', {
          detail: { 
            clientId: accountData.ClientID,
            action: 'created',
            account: response.data.data 
          }
        });
        window.dispatchEvent(refreshEvent);
        
        // También llamar función global si existe (para compatibilidad)
        if (window.refreshAccountsCache && typeof window.refreshAccountsCache === 'function') {
          window.refreshAccountsCache(accountData.ClientID);
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error creating account:', error);
      throw error;
    }
  }

  // Actualizar cuenta existente
  async update(accountId, accountData) {
    try {
      const response = await apiClient.put(`/budget/accounts/${accountId}`, accountData);
      
      // *** DISPARAR EVENTO DE REFRESH TAMBIÉN PARA UPDATES ***
      if (response.data?.success) {

        
        const refreshEvent = new CustomEvent('accounts:refresh', {
          detail: { 
            clientId: accountData.ClientID,
            action: 'updated',
            accountId: accountId,
            account: response.data.data 
          }
        });
        window.dispatchEvent(refreshEvent);
        
        // También llamar función global si existe
        if (window.refreshAccountsCache && typeof window.refreshAccountsCache === 'function') {
          window.refreshAccountsCache(accountData.ClientID);
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error updating account:', error);
      throw error;
    }
  }

  // Eliminar cuenta
  async delete(accountId) {
    try {
      // Primero obtener información de la cuenta para saber qué cliente invalidar
      const accountInfo = await this.getAccountById(accountId);
      
      const response = await apiClient.delete(`/budget/accounts/${accountId}`);
      
      // *** DISPARAR EVENTO DE REFRESH PARA DELETES ***
      if (response.data?.success && accountInfo?.client?.ClientID) {

        
        const refreshEvent = new CustomEvent('accounts:refresh', {
          detail: { 
            clientId: accountInfo.client.ClientID,
            action: 'deleted',
            accountId: accountId
          }
        });
        window.dispatchEvent(refreshEvent);
        
        // También llamar función global si existe
        if (window.refreshAccountsCache && typeof window.refreshAccountsCache === 'function') {
          window.refreshAccountsCache(accountInfo.client.ClientID);
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  }

  // Alternar estado de cuenta (activar/desactivar)
  async toggleAccountStatus(accountId, newStatus) {
    try {
      // Usar el endpoint de actualización para cambiar solo el estado Active
      const response = await apiClient.put(`/budget/accounts/${accountId}`, {
        Active: newStatus
      });
      
      if (response.data?.success) {

        
        // *** DISPARAR EVENTO DE REFRESH PARA TOGGLE STATUS ***
        const accountInfo = await this.getAccountById(accountId);
        if (accountInfo?.success && accountInfo.account?.ClientID) {
          const refreshEvent = new CustomEvent('accounts:refresh', {
            detail: { 
              clientId: accountInfo.account.ClientID,
              action: 'toggled',
              accountId: accountId,
              newStatus: newStatus
            }
          });
          window.dispatchEvent(refreshEvent);
        }
        
        return { success: true, account: response.data.data };
      }
      
      return response.data;
    } catch (error) {
      console.error('Error toggling account status:', error);
      const message = this.handleApiError(error, 'Error al cambiar el estado de la cuenta');
      return { success: false, error: message };
    }
  }

  // === MÉTODOS ALIAS PARA COMPATIBILIDAD ===

  // Alias para create - usado en AccountManagement.js
  async createAccount(accountData) {
    return await this.create(accountData);
  }

  // Alias para update - usado en AccountManagement.js
  async updateAccount(accountId, accountData) {
    return await this.update(accountId, accountData);
  }

  // Alias para delete - usado en AccountManagement.js
  async deleteAccount(accountId) {
    return await this.delete(accountId);
  }

  // === MÉTODOS AUXILIARES ===

  // Formatear cliente para dropdown
  formatClientForDropdown(client) {
    return {
      value: client.ClientID,
      label: client.ClientName,
      id: client.ClientID,
      name: client.ClientName
    };
  }

  // Formatear cuenta para dropdown
  formatAccountForDropdown(account) {
    return {
      value: account.AccountID,
      label: `${account.AccountName} (${account.AccountType})`,
      id: account.AccountID,
      name: account.AccountName,
      type: account.AccountType,
      identifier: account.AccountIdentifier
    };
  }
}

// Exportar instancia singleton
const clientService = new ClientService();
export default clientService; 